import type { Color, Frame, Layer } from "#canvas/types.ts";
import { isStateId } from "#puf/helpers.ts";
import type { StateFrameRanges, StateId } from "#puf/machine.ts";
import { frameRange } from "#puf/machine.ts";
import { STARTER_MACHINE, STARTER_STATE_FRAMES } from "#puf/machines/starter.ts";
import type { MachineLayout, Pxpf, PxpfAnimation, PxpfFrame, PxpfLayer, StateMachine } from "@pixelpuf/schemas/pxpf";
import { PxpfSchema } from "@pixelpuf/schemas/pxpf";

export type { Pxpf } from "@pixelpuf/schemas/pxpf";

const VERSION = 1;
const TILE_SIZE = 8;
const TRANSPARENT = "";

interface EncodeInput {
  name: string;
  rows: number;
  cols: number;
  fps: number;
  frames: Frame[];
  durations?: number[];
  stateFrames?: StateFrameRanges;
  stateMachine?: StateMachine | null;
  machineLayout?: MachineLayout;
}

function encodeRLE(indices: number[]): string {
  if (indices.length === 0) return "";
  const first = indices[0];
  if (first === undefined) return "";
  const out: string[] = [];
  let cur = first;
  let run = 1;
  for (let i = 1; i < indices.length; i++) {
    const v = indices[i];
    if (v === undefined) continue;
    if (v === cur) {
      run++;
    } else {
      out.push(`${cur}:${run}`);
      cur = v;
      run = 1;
    }
  }
  out.push(`${cur}:${run}`);
  return out.join(" ");
}

function decodeRLE(rle: string): number[] {
  if (!rle) return [];
  const out: number[] = [];
  for (const tok of rle.split(" ")) {
    const parts = tok.split(":");
    const iStr = parts[0];
    const cStr = parts[1];
    if (iStr === undefined || cStr === undefined) continue;
    const idx = Number(iStr);
    const count = Number(cStr);
    if (!Number.isFinite(idx) || !Number.isFinite(count)) continue;
    for (let i = 0; i < count; i++) out.push(idx);
  }
  return out;
}

function makeEmptyData(rows: number, cols: number): Color[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
}

export function encode(input: EncodeInput): Pxpf {
  const { name, rows, cols, fps, frames } = input;
  const tileSize = rows % TILE_SIZE === 0 && cols % TILE_SIZE === 0 ? TILE_SIZE : 1;

  // 1. palette
  const colorIndex = new Map<string, number>();
  colorIndex.set(TRANSPARENT, 0);
  const palette: string[] = [TRANSPARENT];
  for (const frame of frames) {
    for (const layer of frame) {
      for (const row of layer.data) {
        for (const cell of row) {
          const c = cell || TRANSPARENT;
          if (!colorIndex.has(c)) {
            colorIndex.set(c, palette.length);
            palette.push(c);
          }
        }
      }
    }
  }

  // 2. layer metadata (visibility comes straight off Layer.visible now)
  const layerOrder: string[] = [];
  const layerProps = new Map<string, PxpfLayer>();
  for (const frame of frames) {
    for (const layer of frame) {
      if (!layerProps.has(layer.id)) {
        layerOrder.push(layer.id);
        layerProps.set(layer.id, {
          id: layer.id,
          name: layer.name ?? layer.id,
          visible: layer.visible,
          opacity: layer.opacity ?? 1,
        });
      }
    }
  }

  // 3. tile + layerFrame dedup
  const tileIndex = new Map<string, number>();
  const tiles: string[] = [];
  const layerFrameIndex = new Map<string, number>();
  const layerFrames: { tilemap: number[] }[] = [];

  function encodeLayerFrame(data: Color[][]): number {
    const tilemap: number[] = [];
    for (let ty = 0; ty < rows; ty += tileSize) {
      for (let tx = 0; tx < cols; tx += tileSize) {
        const indices: number[] = [];
        for (let y = 0; y < tileSize; y++) {
          const row = data[ty + y];
          for (let x = 0; x < tileSize; x++) {
            const cell = row?.[tx + x];
            const c = cell ?? TRANSPARENT;
            indices.push(colorIndex.get(c) ?? 0);
          }
        }
        const rle = encodeRLE(indices);
        let tIdx = tileIndex.get(rle);
        if (tIdx === undefined) {
          tIdx = tiles.length;
          tiles.push(rle);
          tileIndex.set(rle, tIdx);
        }
        tilemap.push(tIdx);
      }
    }
    const key = tilemap.join(",");
    let lfIdx = layerFrameIndex.get(key);
    if (lfIdx === undefined) {
      lfIdx = layerFrames.length;
      layerFrames.push({ tilemap });
      layerFrameIndex.set(key, lfIdx);
    }
    return lfIdx;
  }

  // 4. frames
  const outFrames: PxpfFrame[] = frames.map((frame, i) => {
    const layersMap: Record<string, number> = {};
    for (const layer of frame) {
      layersMap[layer.id] = encodeLayerFrame(layer.data);
    }
    return {
      duration: input.durations?.[i] ?? Math.round(1000 / Math.max(1, fps)),
      layers: layersMap,
    };
  });

  // 5. animations from current state frame ranges. With user-defined state ids
  // we iterate Object.entries instead of a fixed STATE_IDS list — every range
  // becomes one animation entry keyed by its state id.
  const ranges = input.stateFrames ?? STARTER_STATE_FRAMES;
  const animations: PxpfAnimation[] = [];
  for (const [id, range] of Object.entries(ranges)) {
    animations.push({ name: id, from: range[0], to: range[1], fps, loop: "forward" });
  }

  return {
    v: VERSION,
    name,
    canvas: { rows, cols },
    tileSize,
    palette,
    tiles,
    layerFrames,
    layers: layerOrder.map((id) => layerProps.get(id) ?? { id, name: id, visible: true, opacity: 1 }),
    frames: outFrames,
    animations,
    stateMachine: input.stateMachine ?? STARTER_MACHINE,
    machineLayout: input.machineLayout ?? {},
    variants: [{ id: "default" }],
  };
}

interface DecodeOutput {
  name: string;
  rows: number;
  cols: number;
  fps: number;
  frames: Frame[];
  durations: number[];
  stateFrames: Partial<Record<StateId, ReturnType<typeof frameRange>>>;
}

export function decode(pxpf: Pxpf): DecodeOutput {
  const { canvas, tileSize, palette, tiles, layerFrames, frames, layers } = pxpf;
  const { rows, cols } = canvas;

  const tilePixels: number[][] = tiles.map(decodeRLE);
  const colsInTiles = Math.max(1, Math.floor(cols / tileSize));

  const layerFrameGrids: Color[][][] = layerFrames.map((lf) => {
    const grid = makeEmptyData(rows, cols);
    for (let i = 0; i < lf.tilemap.length; i++) {
      const tIdx = lf.tilemap[i];
      if (tIdx === undefined) continue;
      const tile = tilePixels[tIdx];
      if (!tile) continue;
      const ty = Math.floor(i / colsInTiles) * tileSize;
      const tx = (i % colsInTiles) * tileSize;
      for (let y = 0; y < tileSize; y++) {
        for (let x = 0; x < tileSize; x++) {
          const pIdx = tile[y * tileSize + x];
          if (pIdx === undefined) continue;
          const color = palette[pIdx] ?? TRANSPARENT;
          const row = grid[ty + y];
          if (!row) continue;
          row[tx + x] = color;
        }
      }
    }
    return grid;
  });

  const outFrames: Frame[] = frames.map((f) =>
    layers.map<Layer>((meta) => {
      const lfIdx = f.layers[meta.id];
      const data = lfIdx !== undefined ? layerFrameGrids[lfIdx] : undefined;
      return {
        ...meta,
        data: data ?? makeEmptyData(rows, cols),
      };
    })
  );

  const stateFrames: Partial<Record<StateId, ReturnType<typeof frameRange>>> = {};
  for (const anim of pxpf.animations) {
    if (isStateId(anim.name)) {
      stateFrames[anim.name] = frameRange(anim.from, anim.to);
    }
  }

  return {
    name: pxpf.name,
    rows,
    cols,
    fps: pxpf.animations[0]?.fps ?? 12,
    frames: outFrames,
    durations: frames.map((f) => f.duration),
    stateFrames,
  };
}

export function isPxpf(v: unknown): v is Pxpf {
  return PxpfSchema.safeParse(v).success;
}
